import { clamp, dist, mulMat2, smoothstep, vec2 } from './gridMath';

const DEG = Math.PI / 180;

export const composeCellProps = (base, cell, effects, ctx) => (
    effects.reduce((props, fx) => ({ ...props, ...fx(cell, props, ctx) }), { ...base })
);

// value = offset + scrollY×perPx + col×perCol + row×perRow
export const evalScrollAxis = (spec, cell, scrollY) => {
    if (!spec) return 0;
    const { offset = 0, perPx = 0, perCol = 0, perRow = 0 } = spec;
    return offset + scrollY * perPx + cell.col * perCol + cell.row * perRow;
};

const applyAxis = (spec, cell, scrollY, props, apply) => {
    if (!spec) return;
    apply(evalScrollAxis(spec, cell, scrollY), props);
};

// Scroll-driven patches for any cell property (see HOME_SCROLL in patterns.js).
export const scrollCellEffect = (config = {}) => (cell, props, ctx) => {
    const scrollY = ctx.scrollY ?? 0;
    const patch = {};

    applyAxis(config.rotation, cell, scrollY, props, (v, p) => {
        const rad = config.rotation.unit === 'rad' ? v : v * DEG;
        patch.rotation = (p.rotation ?? 0) + rad;
    });

    applyAxis(config.opacity, cell, scrollY, props, (v, p) => {
        const max = config.opacity.max ?? 1;
        if (config.opacity.mode === 'set') patch.opacity = clamp(v, 0, max);
        else patch.opacity = clamp((p.opacity ?? 0) + v, 0, max);
    });

    applyAxis(config.drawScale, cell, scrollY, props, (v, p) => {
        const base = p.drawScale ?? 1;
        patch.drawScale = config.drawScale.mode === 'mul' ? base * v : base + v;
    });

    applyAxis(config.strokeWidth, cell, scrollY, props, (v, p) => {
        const base = p.strokeWidth ?? 1;
        if (config.strokeWidth.mode === 'mul') patch.strokeWidth = base * (1 + v);
        else if (config.strokeWidth.mode === 'set') patch.strokeWidth = v;
        else patch.strokeWidth = base + v;
    });

    applyAxis(config.crossInset, cell, scrollY, props, (v, p) => {
        const base = p.crossInset ?? ctx.baseCrossInset ?? 0;
        if (config.crossInset.mode === 'set') patch.crossInset = v;
        else patch.crossInset = base + v;
    });

    if (config.stroke) patch.stroke = config.stroke;

    return patch;
};

// Static vertical gradient over the grid height (independent of scroll).
// Drives `crossInset` (smaller inset = bigger diamond — same knob hover uses) and,
// optionally, `opacity` (top→bottom fade). Position is normalized by the cell's
// vertical position over the grid height. Tunables live in HOME_VERTICAL_GRADIENT.
export const verticalSizeGradientEffect = (config = {}) => (cell, props, ctx) => {
    if (config.enabled === false) return {};
    const height = ctx.contentH || 1;
    const pRaw = clamp(cell.centerY / height, 0, 1);
    const curve = config.curve ?? 1;
    const p = curve !== 1 ? Math.pow(pRaw, curve) : pRaw;
    const top = config.topInset ?? ctx.baseCrossInset ?? 0;
    const bottom = config.bottomInset ?? ctx.baseCrossInset ?? 0;
    const patch = { crossInset: top + (bottom - top) * p };

    if (config.topOpacity != null || config.bottomOpacity != null) {
        const oCurve = config.opacityCurve ?? 1;
        const op = oCurve !== 1 ? Math.pow(pRaw, oCurve) : pRaw;
        const oTop = config.topOpacity ?? 1;
        const oBottom = config.bottomOpacity ?? 1;
        patch.opacity = (props.opacity ?? 1) * (oTop + (oBottom - oTop) * op);
    }

    return patch;
};

export const cursorProximityEffect = ({
    radius = 200,
    falloffStart = 0,
    falloffEnd = null,
    opacityBoost = 0.5,
    maxOpacity = 1,
    scaleAtCenter = 1,
    scale,
    crossInsetAtCenter = null,
    strokeWidthBoost = 0.5,
    stroke = null,
    matrix = [1, 0, 0, 1],
} = {}) => (cell, props, ctx) => {
    if (!ctx.cursor) return {};

    const sample = mulMat2(matrix, vec2(cell.centerX, cell.centerY));
    const end = falloffEnd ?? radius;
    const t = 1 - smoothstep(falloffStart, end, dist(sample, ctx.cursor));
    if (t <= 0) return {};

    const peakScale = scaleAtCenter ?? scale ?? 1;
    const patch = {
        opacity: clamp((props.opacity ?? 0) + opacityBoost * t, 0, maxOpacity),
        strokeWidth: (props.strokeWidth ?? 1) * (1 + strokeWidthBoost * t),
        drawScale: 1 + (peakScale - 1) * t,
    };

    if (stroke) patch.stroke = stroke;

    if (crossInsetAtCenter != null && ctx.baseCrossInset != null) {
        patch.crossInset = ctx.baseCrossInset + (crossInsetAtCenter - ctx.baseCrossInset) * t;
    }

    return patch;
};
